import { forwardRef, type InputHTMLAttributes, type LabelHTMLAttributes } from 'react';
import type { ChoiceName } from './fieldLabel';
import './Field.css';

export type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'aria-label' | 'aria-labelledby'> &
  ChoiceName & {
    /** Attributes for the wrapping <label> -- the row the user taps (e.g. a data-testid that
     *  e2e measures). Everything else goes to the <input>. */
    labelProps?: LabelHTMLAttributes<HTMLLabelElement> & { [dataAttr: `data-${string}`]: string | undefined };
    /** An option card: a bordered 44 px row on every pointer (the rail's readings and items, used with a Pencil);
     *  a checked radio card takes the --choice-accent edge. */
    card?: boolean;
  };

/**
 * Radio primitive: a native radio and its label in one row. Group them by sharing a `name`. The row (the wrapping <label>) is the
 * target: at least --bk-control-md tall (44 px under any coarse pointer), a bare box 44 x 44 (Field.css). Always
 * named: visible `label`, or an aria name for a bare box (ChoiceName).
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, className = '', disabled, labelProps, card, ...props },
  ref
) {
  return (
    <label {...labelProps} className={['radio', 'choice', card ? 'choice--card' : '', disabled ? 'is-disabled' : '', className].filter(Boolean).join(' ')}>
      <input ref={ref} type="radio" disabled={disabled} {...props} />
      {label != null && <span>{label}</span>}
    </label>
  );
});
